import {
  useState,
} from 'react';
import {
  TextInput,
  Panel,
  explainFailure,
  formatDate,
  useLoad,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import { t } from '../../messages.ts';

/**
 * Migration 0111 — the AMS-side half of a self-signup's approval gate.
 * Approve clears `approvalBlocksBrandCreation` (setup.ts) so the tenant can
 * register a brand; reject leaves it inert indefinitely. Self-contained
 * (own fetch + refresh) so `Dashboard` only needs to know the count, for
 * the aside/no-aside layout decision.
 */
export function OperatorApprovalsPanel({ api }: { api: PlatformApi }) {
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const pending = useLoad(() => api.pendingOperators(), [version]);

  if (pending.state !== 'ready' || pending.value.items.length === 0) return null;
  const items = pending.value.items;

  const decide = (id: string, action: 'approve' | 'reject') => {
    setBusy(id);
    (action === 'approve' ? api.approveOperator(id) : api.rejectOperator(id, reasons[id]?.trim() || undefined))
      .then(() => setVersion((v) => v + 1))
      .catch((e: unknown) => window.alert(explainFailure(e).message))
      .finally(() => setBusy(null));
  };

  return (
    <Panel title={t('overview.approvals.title')} aside={<span className="count count-badge">{items.length}</span>}>
      {items.map((o) => (
        <article key={o.id} className="offer-card">
          <h3 className="offer-title">{o.display_name}</h3>
          <p className="offer-note">
            {t('overview.approvals.signedUp', { date: formatDate(o.joined), currency: o.base_currency })}
            {o.owner_name ? ` — ${o.owner_name}` : ''}
            {o.owner_email ? ` (${o.owner_email})` : ''}
            {o.owner_phone ? `, ${o.owner_phone}` : ''}
          </p>
          <TextInput
            value={reasons[o.id] ?? ''}
            onChange={(v) => setReasons((r) => ({ ...r, [o.id]: v }))}
            placeholder={t('overview.approvals.reasonPlaceholder')}
          />
          <div className="offer-actions">
            <button className="primary" disabled={busy === o.id} onClick={() => decide(o.id, 'approve')}>
              {t('overview.approvals.approve')}
            </button>
            <button className="copy" disabled={busy === o.id} onClick={() => decide(o.id, 'reject')}>
              {t('overview.approvals.deny')}
            </button>
          </div>
        </article>
      ))}
    </Panel>
  );
}
